<template>
  <tr
    class="hover:bg-gray-100 transition-colors duration-150 ease-in-out text-right child:py-4 first-child:pl-16 last-child:pr-3 text-sm cursor-pointer"
    v-bind="$attrs"
    :class="
      {
        'bg-gray-50 border-gray-100': depth <= 1,
        'bg-gray-200 border-gray-300': depth === 2,
        'bg-gray-300 border-gray-400': depth === 3,
        'bg-gray-400 border-gray-500': depth >= 4
      }
    "
    @click="showChildren = !showChildren"
  >
    <td class="text-left">
      <div
        class="flex items-start space-x-2 space-y-1"
        :style="{ paddingLeft: `${(depth * 20) + 8}px` }"
      >
        <button
          v-if="tradeItem.has_children"
          data-test="open-trade-item"
          type="button"
          class="btn-icon btn-icon-default"
          @click.stop="showChildren = !showChildren"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5 transition-all duration-200"
            :class="[{'rotate-90': showChildren}]">
  <path fill-rule="evenodd" d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z" clip-rule="evenodd" />
</svg>
        </button>

        <p class="w-60 text-gray-700">
          {{ tradeItem.name }}
        </p>
      </div>
    </td>

    <td class="pr-3">
      {{ $tcc(childBudget) }}
    </td>

    <td class="bg-green-200/50">
      {{ $tcc(childClaim) }}
    </td>

    <td class="bg-green-200/50">
      {{ $tcc(childCertified) }}
    </td>

    <td
      class="bg-green-200/50"
      :class="{ 'text-red-600': hasVariance && variance !== 0 }"
    >
      {{ hasVariance ? $tcc(variance) : '' }}
    </td>

    <td class="bg-green-200/50">
      {{ $tcc(childPrevious) }}
    </td>

    <td class="bg-green-200/50 pr-3">
      {{ $tcc(childPrevious + childCertified) }}
    </td>

    <td class="bg-blue-100/50">
      <span v-if="showPercentages">
        {{ childBudget > 0 ? $tp((childClaim / childBudget) * 100) : '' }}
      </span>
    </td>

    <td
      v-if="showPercentages"
      class="bg-blue-100/50"
    >
      {{ childBudget > 0 ? $tp((childCertified / childBudget) * 100) : '' }}
    </td>

    <td
      v-if="showPercentages"
      class="bg-blue-100/50"
    >
      {{ childBudget > 0 ? $tp((childPrevious / childBudget) * 100) : '' }}
    </td>

    <td
      v-if="showPercentages"
      class="bg-blue-100/50 pr-3"
    >
      {{ childBudget > 0 ? $tp(((childPrevious + childCertified) / childBudget) * 100) : '' }}
    </td>

    <td class="pr-3">
      {{ $tcc(Math.abs(childBudget - childPrevious - childCertified)) }}
    </td>

    <td class="bg-orange-200/30 relative z-10">
      <span v-if="showForecasts">{{ $tcc(childBaselineForecast) }}</span>
    </td>

    <td
      v-if="showForecasts"
      class="bg-orange-200/30 relative z-10 pr-3"
    >
      {{ $tcc(childActualForecast) }}
    </td>
  </tr>

  <template v-if="relevantChildren.length > 0 && showChildren">
    <LumpSumTradeRowItem
      v-for="childItem in relevantChildren"
      :key="childItem.id"
      :trade="$props.trade"
      :claim="$props.claim"
      :trade-item="childItem"
      :project-id="$props.projectId"
      :package-id="$props.packageId"
      :can-edit-certified="$props.canEditCertified"
      :can-edit-claim="$props.canEditClaim"
      :lite-mode="$props.liteMode"
      :show-percentages="$props.showPercentages"
      :show-forecasts="$props.showForecasts"
      :depth="depth ? depth + 1 : 1"
    />
  </template>
</template>

<script setup lang="ts">
import Claim from '@/models/Claim';
import PackageTradeItem from '@/models/PackageTradeItem';
import Trade from '@/models/Trade';
import TradeItem from '@/models/TradeItem';
import { useLumpSumCostsStore } from '@/store/lumpSumCosts';
import { usePackageTradeItemsStore } from '@/store/packageTradeItems';
import { useTradeItemsStore } from '@/store/tradeItems';
import { computed, ref } from 'vue';
import LumpSumTradeRowItem from './LumpSumTradeRowItem.vue';

const props = withDefaults(
  defineProps<{
    trade?: Trade;
    claim: Claim;
    tradeItem: TradeItem;
    projectId: string;
    packageId: string;
    canEditCertified: boolean;
    canEditClaim: boolean;
    liteMode?: boolean;
    showPercentages?: boolean;
    showForecasts?: boolean;
    depth?: number;
  }>(),
  {
    liteMode: false,
    depth: 0,
  },
);

defineOptions({
  inheritAttrs: false,
});

const tradeItemsStore = useTradeItemsStore();
const packageTradeItemsStore = usePackageTradeItemsStore();
const lumpSumCostsStore = useLumpSumCostsStore();

const packageTradeItemIds = computed(() => {
  return packageTradeItemsStore.models
    .where('package_id', parseInt(props.packageId))
    .get()
    .map((packageTradeItem) => packageTradeItem.trade_item_id);
});

const hasPackageDescendant = (tradeItemId: Id): boolean => {
  if(packageTradeItemIds.value.some((id) => id === tradeItemId)) {
    return true;
  }

  return tradeItemsStore.models
    .where('parent_id', tradeItemId as number)
    .get()
    .some((child) => hasPackageDescendant(child.id));
};

const relevantChildren = computed(() => {
  return tradeItemsStore.models
    .where('parent_id', props.tradeItem.id)
    .orderBy('sort_order')
    .get()
    .filter((child) => hasPackageDescendant(child.id));
});

const descendantPackageTradeItems = computed((): PackageTradeItem[] => {
  const collect = (tradeItem: TradeItem): PackageTradeItem[] => {
    if(!tradeItem.has_children) {
      const packageTradeItem = packageTradeItemsStore.models
        .where('package_id', parseInt(props.packageId))
        .where('trade_item_id', tradeItem.id)
        .first();

      return packageTradeItem ? [packageTradeItem] : [];
    }

    return tradeItemsStore.models
      .where('parent_id', tradeItem.id)
      .get()
      .flatMap((child) => collect(child));
  };

  return collect(props.tradeItem);
});

const showChildren = ref(relevantChildren.value.length > 0);

const childBudget = computed(() => {
  return descendantPackageTradeItems.value.reduce((total, packageTradeItem) => {
    return total + packageTradeItem.value;
  }, 0);
});

const childClaim = computed(() => {
  return descendantPackageTradeItems.value.reduce((total, packageTradeItem) => {
    const lumpSumCost = lumpSumCostsStore.models
      .where('trade_item_id', packageTradeItem.trade_item_id)
      .where('claim_id', props.claim.id)
      .first();

    return total + (lumpSumCost?.current_claim || 0);
  }, 0);
});

const childCertified = computed(() => {
  return descendantPackageTradeItems.value.reduce((total, packageTradeItem) => {
    const lumpSumCost = lumpSumCostsStore.models
      .where('trade_item_id', packageTradeItem.trade_item_id)
      .where('claim_id', props.claim.id)
      .first();

    return total + (lumpSumCost?.current_certified || 0);
  }, 0);
});

const childPrevious = computed(() => {
  return descendantPackageTradeItems.value.reduce((total, packageTradeItem) => {
    return total + packageTradeItem.previous(props.claim);
  }, 0);
});

const childBaselineForecast = computed(() => {
  return descendantPackageTradeItems.value.reduce((total, packageTradeItem) => {
    return total + (packageTradeItem.baseline_forecast_amount || 0);
  }, 0);
});

const childActualForecast = computed(() => {
  return descendantPackageTradeItems.value.reduce((total, packageTradeItem) => {
    return total + (packageTradeItem.actualForecastForClaim(props.claim) || 0);
  }, 0);
});

const variance = computed(() => {
  return childClaim.value - childCertified.value;
});

const hasVariance = computed(() => {
  return true;
});
</script>

<style scoped></style>
